import { lazy, Suspense } from 'react';
import { Outlet } from 'react-router-dom';

import { AuthGuard } from 'src/auth/guard';
import DashboardLayout from 'src/layouts/dashboard';

import { LoadingScreen } from 'src/components/loading-screen';

import PracticeRosterPage from '../../pages/dashboard/admin/practice-roster';

// ----------------------------------------------------------------------

// OVERVIEW
const IndexPage = lazy(() => import('src/pages/dashboard/app'));
const OverviewEcommercePage = lazy(() => import('src/pages/dashboard/ecommerce'));
// const OverviewAnalyticsPage = lazy(() => import('src/pages/dashboard/analytics'));
const OverviewBankingPage = lazy(() => import('src/pages/dashboard/banking'));
const OverviewBookingPage = lazy(() => import('src/pages/dashboard/booking'));
const OverviewFilePage = lazy(() => import('src/pages/dashboard/file'));
// PRODUCT
const ProductDetailsPage = lazy(() => import('src/pages/dashboard/product/details'));
const ProductListPage = lazy(() => import('src/pages/dashboard/product/list'));
const ProductCreatePage = lazy(() => import('src/pages/dashboard/product/new'));
const ProductEditPage = lazy(() => import('src/pages/dashboard/product/edit'));
// ORDER
const OrderListPage = lazy(() => import('src/pages/dashboard/order/list'));
const OrderDetailsPage = lazy(() => import('src/pages/dashboard/order/details'));
// INVOICE
const InvoiceListPage = lazy(() => import('src/pages/dashboard/invoice/list'));
const InvoiceDetailsPage = lazy(() => import('src/pages/dashboard/invoice/details'));
const InvoiceCreatePage = lazy(() => import('src/pages/dashboard/invoice/new'));
const InvoiceEditPage = lazy(() => import('src/pages/dashboard/invoice/edit'));
// USER
const UserProfilePage = lazy(() => import('src/pages/dashboard/user/profile'));
const UserCardsPage = lazy(() => import('src/pages/dashboard/user/cards'));
const UserAccountPage = lazy(() => import('src/pages/dashboard/user/account'));
const UserCreatePage = lazy(() => import('src/pages/dashboard/user/new'));
const UserEditPage = lazy(() => import('src/pages/dashboard/user/edit'));
// BLOG
const BlogPostsPage = lazy(() => import('src/pages/dashboard/post/list'));
const BlogPostPage = lazy(() => import('src/pages/dashboard/post/details'));
const BlogNewPostPage = lazy(() => import('src/pages/dashboard/post/new'));
const BlogEditPostPage = lazy(() => import('src/pages/dashboard/post/edit'));
// JOB
const JobDetailsPage = lazy(() => import('src/pages/dashboard/job/details'));
const JobListPage = lazy(() => import('src/pages/dashboard/job/list'));
const JobCreatePage = lazy(() => import('src/pages/dashboard/job/new'));
const JobEditPage = lazy(() => import('src/pages/dashboard/job/edit'));
// TOUR
const TourDetailsPage = lazy(() => import('src/pages/dashboard/tour/details'));
const TourListPage = lazy(() => import('src/pages/dashboard/tour/list'));
const TourCreatePage = lazy(() => import('src/pages/dashboard/tour/new'));
const TourEditPage = lazy(() => import('src/pages/dashboard/tour/edit'));
// FILE MANAGER
const FileManagerPage = lazy(() => import('src/pages/dashboard/file-manager'));
// APP
const ChatPage = lazy(() => import('src/pages/dashboard/chat'));
const MailPage = lazy(() => import('src/pages/dashboard/mail'));
const CalendarPage = lazy(() => import('src/pages/dashboard/calendar'));
const KanbanPage = lazy(() => import('src/pages/dashboard/kanban'));
// TEST RENDER PAGE BY ROLE
const PermissionDeniedPage = lazy(() => import('src/pages/dashboard/permission'));
// BLANK PAGE
const BlankPage = lazy(() => import('src/pages/dashboard/blank'));
// HELP CENTER PAGE
const HelpCenter = lazy(() => import('src/pages/dashboard/help-center'));
const ReferralRequestsProvider = lazy(() => import('src/pages/dashboard/provider/referral-requests'));
const CaseloadProviderPage = lazy(() => import('src/pages/dashboard/provider/provider-caseload'));
const ProviderRosterPage = lazy(() => import('src/pages/dashboard/admin/provider-roster'));
const ProviderProfilePage = lazy(() => import('src/pages/dashboard/provider/provider-profile'));
const ClientIntakePage = lazy(() => import('src/pages/dashboard/admin/client-intake'));
const SupervisorAnalytics = lazy(() => import('src/pages/dashboard/provider/analytics/supervisor-analytics'));
const AnalyticsPage = lazy(() => import('src/pages/dashboard/provider/analytics/provider-analytics'));
const TreatmentPlansAnalytics = lazy(() => import('src/pages/dashboard/provider/analytics/treatment-plan-analytics'));
const MissingNotesAnalytics = lazy(() => import('src/pages/dashboard/provider/analytics/missing-notes-analytics'));
const DischargeAnalytics = lazy(() => import('src/pages/dashboard/provider/analytics/discharge-analytics'));
const PracticeProviderRoster = lazy(() => import('src/pages/dashboard/practice/practice-provider-roster'));
const PracticeClientList = lazy(() => import('src/pages/dashboard/practice/practice-client-list'));
const PracticeProfile = lazy(() => import('src/pages/dashboard/practice/practice-profile'));
const GroupsRoster = lazy(() => import('src/pages/dashboard/admin/groups-roster'));
const ClientMatching = lazy(() => import('src/pages/dashboard/admin/client-matching/client-matching'));

// ----------------------------------------------------------------------

export const dashboardRoutes = [
  {
    path: 'dashboard',
    element: (
      <AuthGuard>
        <DashboardLayout>
          <Suspense fallback={<LoadingScreen />}>
            <Outlet />
          </Suspense>
        </DashboardLayout>
      </AuthGuard>
    ),
    children: [
      { path: 'provider-roster', element: <ProviderRosterPage /> },
      { path: 'practice-roster', element: <PracticeRosterPage />},
      { path: 'provider-caseload', element: <CaseloadProviderPage /> },
      { path: 'referral-requests', element: <ReferralRequestsProvider /> },
      { path: 'provider-profile', element: <ProviderProfilePage /> },
      { path: 'client-intake', element: <ClientIntakePage /> },
      { element: <IndexPage />, index: true },
      { path: 'analytics',
        children: [
          { element: <AnalyticsPage />, index: true },
          { path: 'missing-notes', element: <MissingNotesAnalytics /> },
          { path: 'treatment-plans', element: <TreatmentPlansAnalytics /> },
          { path: 'discharges', element: <DischargeAnalytics /> },
          { path: 'supervisors', element: <SupervisorAnalytics /> },
        ]
      },
      { path: 'practice-provider-roster', element: <PracticeProviderRoster /> },
      { path: 'practice-client-list', element: <PracticeClientList /> },
      { path: 'practice-profile', element: <PracticeProfile /> },
      { path: 'groups-roster', element: <GroupsRoster /> },
      { path: 'client-matching', element: <ClientMatching /> },


      // Pre-made routes from here down

      { path: 'ecommerce', element: <OverviewEcommercePage /> },
      { path: 'banking', element: <OverviewBankingPage /> },
      { path: 'booking', element: <OverviewBookingPage /> },
      { path: 'file', element: <OverviewFilePage /> },
      {
        path: 'user',
        children: [
          { element: <UserProfilePage />, index: true },
          { path: 'profile', element: <UserProfilePage /> },
          { path: 'cards', element: <UserCardsPage /> },
          { path: 'new', element: <UserCreatePage /> },
          { path: ':id/edit', element: <UserEditPage /> },
          { path: 'account', element: <UserAccountPage /> },
        ],
      },
      {
        path: 'product',
        children: [
          { element: <ProductListPage />, index: true },
          { path: 'list', element: <ProductListPage /> },
          { path: ':id', element: <ProductDetailsPage /> },
          { path: 'new', element: <ProductCreatePage /> },
          { path: ':id/edit', element: <ProductEditPage /> },
        ],
      },
      {
        path: 'order',
        children: [
          { element: <OrderListPage />, index: true },
          { path: 'list', element: <OrderListPage /> },
          { path: ':id', element: <OrderDetailsPage /> },
        ],
      },
      {
        path: 'invoice',
        children: [
          { element: <InvoiceListPage />, index: true },
          { path: 'list', element: <InvoiceListPage /> },
          { path: ':id', element: <InvoiceDetailsPage /> },
          { path: ':id/edit', element: <InvoiceEditPage /> },
          { path: 'new', element: <InvoiceCreatePage /> },
        ],
      },
      {
        path: 'post',
        children: [
          { element: <BlogPostsPage />, index: true },
          { path: 'list', element: <BlogPostsPage /> },
          { path: ':title', element: <BlogPostPage /> },
          { path: ':title/edit', element: <BlogEditPostPage /> },
          { path: 'new', element: <BlogNewPostPage /> },
        ],
      },
      {
        path: 'job',
        children: [
          { element: <JobListPage />, index: true },
          { path: 'list', element: <JobListPage /> },
          { path: ':id', element: <JobDetailsPage /> },
          { path: 'new', element: <JobCreatePage /> },
          { path: ':id/edit', element: <JobEditPage /> },
        ],
      },
      {
        path: 'tour',
        children: [
          { element: <TourListPage />, index: true },
          { path: 'list', element: <TourListPage /> },
          { path: ':id', element: <TourDetailsPage /> },
          { path: 'new', element: <TourCreatePage /> },
          { path: ':id/edit', element: <TourEditPage /> },
        ],
      },
      { path: 'file-manager', element: <FileManagerPage /> },
      { path: 'mail', element: <MailPage /> },
      { path: 'chat', element: <ChatPage /> },
      { path: 'calendar', element: <CalendarPage /> },
      { path: 'kanban', element: <KanbanPage /> },
      { path: 'permission', element: <PermissionDeniedPage /> },
      { path: 'blank', element: <BlankPage /> },
      { path: 'help-center', element: <HelpCenter /> },
    ],
  },
];
